import { Injectable } from '@angular/core';
import {
  EmptyBedReasonsInput,
  UpdateCapacityReportInput,
} from '@libs/services/graphql/generated/ala-backend.service';
import { EmptyBedReasonsDto } from '../dtos-v2/empty-reasons.dto';
import { CAPACITY_COLUMN_PREFIX } from '../maps-v2/capacity-column-prefix.map';
import { CAPACITY_COLUMN_SUffIX } from '../maps-v2/capacity-column-suffix.map';
import { BedTypeCode } from '../types-v2/bed-type-code';
import { CapacityColumnName } from '../types-v2/columns/capacity-column-full-name.type';
import { CapacityColumnSuffix } from '../types-v2/columns/capacity-column-suffix.type';
import { FacilityBedsRawType } from '../types-v2/raws/facility-beds-raw.type';

@Injectable({ providedIn: 'root' })
export class CapacityInputTransformerService {
  /**
   * Transforms the provided bed type, empty bed reason, and value into an `UpdateCapacityReportInput` object.
   *
   * @param bedType - The type of bed for which the empty reason is being reported.
   * @param reason - The reason why the bed is empty.
   * @param value - The numeric value associated with the empty bed reason.
   * @returns An `UpdateCapacityReportInput` object containing the empty bed reason input for the specified bed type.
   * Transform into:
   * {
   *   <prefix>EmptyBedReasons: {
   *     <emptyBedReason>: value
   *   }
   * }
   */
  transformToEmptyBedReasonInput(
    bedType: BedTypeCode,
    reason: keyof EmptyBedReasonsDto,
    value: number | null
  ): UpdateCapacityReportInput {
    const parentField = this.resolveBedEmptyReasonsField(bedType);

    return {
      [parentField]: {
        [reason]: value,
      } as EmptyBedReasonsInput,
    } as UpdateCapacityReportInput;
  }

  /**
   * Transforms bed type and empty beds status into a capacity report update input.
   * @param bedType - The type of bed to update
   * @param hasEmptyBeds - Boolean indicating whether the bed type has empty beds
   * @returns An UpdateCapacityReportInput object containing the transformed capacity data
   * Transform into:
   * {
   *   <prefix>HasEmptyBeds: true/false
   * }
   */
  transformToHasEmptyBedsInput(
    bedType: BedTypeCode,
    hasEmptyBeds: boolean
  ): UpdateCapacityReportInput {
    const columnName = this.resolveCapacityColumn(
      bedType as BedTypeCode,
      CAPACITY_COLUMN_SUffIX.HasEmptyBeds
    );

    return this.resolveCapacityReportInput<
      FacilityBedsRawType,
      UpdateCapacityReportInput
    >([{ name: columnName, value: hasEmptyBeds }]);
  }

  private resolveBedEmptyReasonsField(
    bedType: BedTypeCode
  ): keyof FacilityBedsRawType {
    const prefix = CAPACITY_COLUMN_PREFIX[bedType];
    return `${prefix}${CAPACITY_COLUMN_SUffIX.EmptyBedReasons}` as keyof FacilityBedsRawType;
  }

  resolveCapacityReportInput<T, R>(
    values: {
      name: keyof T;
      value: T[keyof T];
    }[]
  ): R {
    return Object.fromEntries(
      values.map(({ name, value }) => [name, value])
    ) as R;
  }

  resolveCapacityColumn(
    bedType: BedTypeCode,
    suffix: CapacityColumnSuffix
  ): CapacityColumnName {
    const prefix = CAPACITY_COLUMN_PREFIX[bedType];
    return `${prefix}${suffix}` as CapacityColumnName;
  }
}
